import { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { clearPatientAuth } from '../../redux/slices/patientAuthSlice';
import { useNavigate, useParams } from 'react-router-dom';
import { LogOut, ChevronDown, User } from 'lucide-react';
import { Avatar } from 'react-daisyui';
import { PORTAL_ROUTES } from '../constants';
import { useClickOutside } from '../hooks/useClickOutside';
import { useOnEscape } from '../hooks/useOnEscape';

export const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { resourceType, encodedResourceId } = useParams();
  const { patientInfo, practiceInfo } = useSelector((s: RootState) => s.patientAuth);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const closeDropdown = () => setDropdownOpen(false);
  useClickOutside(dropdownRef, closeDropdown, dropdownOpen);
  useOnEscape(closeDropdown, dropdownOpen);

  const handleSignOut = () => {
    setDropdownOpen(false);
    dispatch(clearPatientAuth());
    if (resourceType && encodedResourceId) {
      navigate(`/${resourceType}/${encodedResourceId}`, { replace: true });
    } else {
      navigate(PORTAL_ROUTES.ROOT, { replace: true });
    }
  };

  const fullName = patientInfo?.fullName || 'Patient';

  const initials = patientInfo?.fullName
    ? patientInfo.fullName
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0])
        .join('')
        .toUpperCase()
    : 'P';

  return (
    <header className="w-full bg-transparent sticky top-0 z-30">
      <div className="h-16 px-8 flex items-center justify-between">
        {/* Left: Practice logo + name */}
        <div className="flex items-center gap-2.5">
          {practiceInfo?.logo ? (
            <Avatar size="xs" src={practiceInfo.logo} shape="circle" />
          ) : (
            <Avatar size="xs" src="/logo.png" />
          )}
          <span className="text-xl font-bold text-secondary-900 tracking-tight">
            {practiceInfo?.name || 'Patient Portal'}
          </span>
        </div>

        {/* Right: Profile */}
        <div className="flex items-center gap-3">
          {/* Profile dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-expanded={dropdownOpen}
              aria-haspopup="menu"
              className="flex items-center gap-2.5 py-1.5 pl-1.5 pr-2 rounded-full hover:bg-gray-50 transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-teamcare-blue text-white flex items-center justify-center text-sm font-bold">
                {initials}
              </div>
              <span className="text-sm font-semibold text-secondary-800 hidden sm:inline">
                {fullName}
              </span>
              <ChevronDown
                size={14}
                className={`text-secondary-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {dropdownOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 animate-scale-in"
              >
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-bold text-secondary-900">{fullName}</p>
                  {patientInfo?.email && (
                    <p className="text-xs text-secondary-400 mt-0.5">{patientInfo.email}</p>
                  )}
                </div>
                <div className="py-1">
                  <button
                    role="menuitem"
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-secondary-700 hover:bg-gray-50 transition-colors"
                  >
                    <User size={16} className="text-secondary-400" />
                    My Profile
                  </button>
                </div>
                <div className="border-t border-gray-100 pt-1">
                  <button
                    role="menuitem"
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
